/** Mirrors the API contract in README §9. Kept hand-written rather than
 *  generated: the shapes are small and stable, and the comments explaining
 *  which field means what are the point.
 */

/** What a stored reading is. The chart draws a counter or status as steps
 *  and QC flags by it; nothing aggregates by it any more — data is served
 *  raw (2026-09-28). */
export type ParameterKind =
  | 'instant'
  | 'counter_lifetime'
  | 'counter_daily'
  | 'direction'
  | 'status'

/** Which client collects a station. `external_id` is read per vendor: the
 *  MDS station_id, the CMT Edge gateway's network id, or the MRL logger's
 *  station id (the SID line of its pushes, docs/MRL_PUSH.md). */
export type Vendor = 'sommer' | 'cmt_edge' | 'mrl_push'

export const VENDOR_LABEL: Record<Vendor, string> = {
  sommer: 'Sommer MDS',
  cmt_edge: 'Worldsensing CMT Edge',
  mrl_push: 'Sommer MRL (direct push)',
}

/** Vendors the add-station probe can ask. An MRL push station is seeded in
 *  SQL: there is no vendor API to probe. */
export const PROBE_VENDORS: Vendor[] = ['sommer', 'cmt_edge']

/** What to call the vendor's id in a label. */
export function externalIdLabel(vendor: Vendor | undefined): string {
  if (vendor === 'cmt_edge') return 'Network id'
  if (vendor === 'mrl_push') return 'Logger id'
  return 'MDS id'
}

/** The station form's label for the id field. */
export function stationIdFieldLabel(vendor: Vendor | undefined): string {
  if (vendor === 'cmt_edge') return 'Gateway network id'
  if (vendor === 'mrl_push') return 'Logger station id'
  return 'MDS station id'
}

export interface Station {
  /** The live projects holding this station that the reader can see
   *  (phase 3). Optional so older fixtures keep compiling. */
  projects?: string[]
  id: number
  code: string
  name: string
  external_id: string
  vendor: Vendor
  timezone: string
  poll_interval_s: number
  schedule_time: string
  catchup_days: number
  schedule_enabled: boolean
  active: boolean
  retention_start: string | null
  last_data_day: string | null
  days_behind: number | null
  failed_days: number
  empty_days: number
  unmapped_channels: number
  mapped_channels: number
  last_measurement_at: string | null
  /** How far back the archive actually goes, which is not `retention_start` —
   *  that is how far back MDS is willing to serve. The gap between them is
   *  backfill still outstanding. */
  first_measurement_at: string | null
  /** Open alerts for this station (db/022), oldest first. Absent from an
   *  API that predates them. */
  alerts?: StationAlert[]
  /** When the health checks last ran; null if never. Older than three
   *  minutes means the alerts are not current (lib/health.ts). */
  health_checked_at?: string | null
  /** How often the health monitor expects this station's readings, and how
   *  long without one before it calls the station silent (backend
   *  intake/health.py, on the station summary since redesign phase 1). The
   *  frontend judges freshness by these and never recomputes them. Absent
   *  from an API that predates them. */
  expected_interval_s?: number
  silent_after_s?: number
  /** The newest real reading the health monitor found (link telemetry and
   *  blanks excluded; null for none in its 30-day lookback), from its last
   *  round (db/027). Absent for a station it does not watch. Unlike
   *  `last_measurement_at`, telemetry cannot keep this fresh. */
  newest_reading_at?: string | null
}

export interface AlertBase { id: number; subject: string; opened_at: string; last_seen_at: string }

export interface SilentDetail { last_reading_at: string | null; expected_s: number; after_s: number }
export interface SourceFailingDetail {
  tasks: { task: string; failing_since: string; last_error: string | null }[]
  gateway: { last_ok_at: string | null; failing_since: string; last_error: string | null } | null
}
export interface IntakeStuckDetail {
  failed: number; oldest_pending_at: string | null; refusals: number
  last_refusal_at: string | null; last_refusal_claimed: string | null
}
export interface ChannelsQuietDetail {
  count: number; channels: { name: string; instrument: string | null; last_at: string }[]
}

/** One open alert at a station (db/022). The health monitor opens it when
 *  its condition holds and closes it 15 minutes after it clears. */
export type StationAlert =
  | (AlertBase & { kind: 'silent'; detail: SilentDetail })
  | (AlertBase & { kind: 'source_failing'; detail: SourceFailingDetail })
  | (AlertBase & { kind: 'intake_stuck'; detail: IntakeStuckDetail })
  | (AlertBase & { kind: 'channels_quiet'; detail: ChannelsQuietDetail })
  | (AlertBase & { kind: 'still_on_mds'; detail: { last_push_at: string } })

// ------------------------------------------------- the Intake page (phase 5)

export interface WorkerBeat { worker_id: string; role: 'fast' | 'slow'; started_at: string; beat_at: string }
export interface QueueRow { task: string; due: number; oldest_due_at: string; limit_s: number | null }

/** An open alert as the Intake page lists it: a station's, or the
 *  platform's (station_code null). */
export type IntakeAlert =
  | (StationAlert & { station_code: string | null; station_name: string | null })
  | (AlertBase & { station_code: null; station_name: null; kind: 'worker_silent';
                   detail: { role: string; last_beat_at: string | null } })
  | (AlertBase & { station_code: null; station_name: null; kind: 'queue_lag';
                   detail: { task: string; due: number; oldest_due_at: string; lag_s: number; limit_s: number } })
  | (AlertBase & { station_code: null; station_name: null; kind: 'disk';
                   detail: { used_fraction: number; total_bytes: number; free_bytes: number } })

export interface IntakeOverview {
  checked_at: string | null
  disk: { total_bytes: number; free_bytes: number } | null
  db_bytes: number | null
  workers: WorkerBeat[]
  queue: QueueRow[]
  alerts: IntakeAlert[]
  pushes: { pending: number; oldest_pending_at: string | null; failed: number }
}

export interface FailedPush {
  id: number; station: string; logger_id: string; received_at: string
  parsed_at: string | null; attempts: number; error: string | null
}

export interface PushRefusal {
  id: number; at: string; station: string | null; logger_id: string | null
  claimed: string | null; reason: 'sid_mismatch' | 'no_station'; remote_addr: string | null
}

export interface ShadowComparison {
  station: string; days: number; pushes: number; failed: number; matched: number
  differing: number; push_only: number; unmapped: number[]
  missing: { parameter: string; readings: number }[]
  differences: { parameter: string; ts: string; stored: number | null; pushed: number | null }[]
  passes: boolean
}

export interface StationParameter {
  code: string
  display_name: string
  unit: string
  /** Charts group Y axes on this, never on unit: water_level and water_depth
   *  are both metres and are different quantities. */
  axis_group: string
  kind: ParameterKind
  channel_id: number
  event_driven: boolean
  source_name: string | null
  source_unit: string | null
}

export interface StationDetail extends Station {
  parameters: StationParameter[]
  instruments: Instrument[]
}

/** One camera photo an MRL logger uploaded (GET /api/stations/{code}/photos).
 *  `id` is the capture's name, which sorts by arrival; `received_at` is when
 *  it arrived — the JPEG carries no time of its own. */
export interface StationPhoto {
  id: string
  received_at: string
  media_type: string
}

export interface LatestReading {
  parameter_code: string
  display_name: string
  unit: string
  axis_group: string
  ts: string
  value: number | null
  quality: number
  instrument_id: number | null
  group: string | null
  channel_no: number | null
  quantity: string | null
  precision: number | null
}

/** The newest stored reading of one channel (GET /api/latest): a dashboard's one
 *  request a minute, however many cards it has. A pair with no value is absent. */
export interface PairReading {
  station: string
  parameter: string
  ts: string
  value: number
  quality: number
  unit: string
  precision: number | null
  kind: ParameterKind
  display_name: string
  axis_group: string
}

/** One instrument at a gateway station — a node, in the gateway's terms. */
export interface Instrument {
  instrument_id: number
  /** The gateway's name for it. Some nodes have none (Zhinvali 151865). */
  name: string | null
  model: string | null
  channel_count: number
  /** When it last reported anything, readings or link telemetry. */
  last_seen: string | null
}

/** One stored channel's newest value. `value` is the string for a text
 *  channel, which has no parameter and cannot be charted. */
export interface Cell {
  quantity: string
  source_column: string
  parameter_code: string | null
  display_name: string
  unit: string
  axis_group: string | null
  kind: ParameterKind | null
  value_type: 'numeric' | 'text'
  value: number | string | null
  /** Decimal places the gateway publishes on this channel. Null on channels
   *  provisioned without one; render those through formatNumber. */
  precision: number | null
  quality: number | null
  ts: string | null
}

/** A channel: a (sensor, channel) pair on a Sisgeo string, a channel on a
 *  VW-5 node, or neither for a node-level quantity. */
export interface ChannelRow {
  sensor_no: number | null
  channel_no: number | null
  cells: Cell[]
}

/** A column group as the gateway states it (LongTermAgg, GPSFix, …).
 *  Null where the family has no groups. */
export interface Group {
  group: string | null
  rows: ChannelRow[]
}

/** One file family (vw, gnss-stats, errors, link:healthV2, …), with its own
 *  receipt time, because families arrive on different cadences. */
export interface Family {
  family: string | null
  received_at: string | null
  groups: Group[]
}

export interface InstrumentDetail {
  instrument_id: number
  name: string | null
  model: string | null
  families: Family[]
}

export interface Parameter {
  code: string
  display_name: string
  unit: string
  axis_group: string
  kind: ParameterKind
  min_valid: number | null
  max_valid: number | null
  description: string | null
}

/** [isoTimestamp, value], plus the quality bitmask when include_quality. */
export type SeriesPoint = [string, number | null] | [string, number | null, number]

export interface Series {
  station: string
  parameter: string
  display_name: string
  unit: string
  axis_group: string
  kind: ParameterKind
  points: SeriesPoint[]
}

/** A project role (db/029). A platform admin is Admin in every project. */
export type ProjectRole = 'viewer' | 'editor' | 'admin'

export type NoDataReason = 'decommissioned' | 'collection_off' | 'never_read' | 'silent'

/** One project as the list shows it (GET /api/projects): only the reader's
 *  projects, every one for a platform admin. */
export interface Project {
  id: number
  name: string
  client: string
  timezone: 'Asia/Tbilisi' | 'UTC'
  crs_epsg: 32638 | 32637 | 4326
  station_count: number
  member_count: number
  /** Its stations Home would call No data, and why (Home's order). With
   *  the health checks stopped Home calls a `silent` one Monitoring delayed. */
  no_data_stations: { name: string; reason: NoDataReason }[]
  /** Open alarms and warnings on its stations (phase 4). */
  alarms?: { alarm: number; warning: number }
  my_role: ProjectRole
  created_at: string
}

export interface ProjectMember {
  user_id: number
  name: string
  email: string
  role: ProjectRole
}

/** One project (GET /api/projects/{id}). */
export interface ProjectDetail {
  project: {
    id: number
    name: string
    client: string
    description: string
    timezone: 'Asia/Tbilisi' | 'UTC'
    crs_epsg: 32638 | 32637 | 4326
    created_at: string
    updated_at: string
  }
  my_role: ProjectRole
  stations: Station[]
  members: ProjectMember[]
}

export interface ProjectMemberIn { email: string; role: ProjectRole }

/** A dated note on a project, optionally on one of its stations (db/043,
 *  GET /api/project-logs). Times are instants; the screen shows them in
 *  station time. `can_edit` is the server's: the author, a project Editor or
 *  Admin, or a platform admin. */
export interface ProjectLog {
  id: number; project_id: number; project_name: string; station_code: string | null; stations: string[]
  starts_at: string; ends_at: string; category: 'note' | 'maintenance' | 'incident'
  note: string; author: string; created_at: string; can_edit: boolean
}
export type ProjectLogWrite = Pick<ProjectLog, 'project_id' | 'station_code' | 'starts_at' | 'ends_at' | 'category' | 'note'>

/** An account a project can be given (GET /api/project-people). */
export interface ProjectPerson { id: number; email: string; name: string }

export interface ProjectWrite {
  name: string
  client?: string
  description?: string
  timezone?: 'Asia/Tbilisi' | 'UTC'
  crs_epsg?: 32638 | 32637 | 4326
}

/** A pin on a chart (GET /api/stations/{code}/pins): one stored reading, or
 *  a stretch of one series from one stored reading to another, with a note,
 *  shared with everyone signed in. `value` and `value_end` are the stored
 *  values the server read back, never the browser's. */
export interface ChartPin {
  id: number
  /** Sent since /api/pins, where one chart spans stations. */
  station?: string
  parameter: string
  ts: string
  ts_end: string | null
  note: string
  value: number | null
  value_end: number | null
  created_by_name: string
  created_at: string
  mine: boolean
}

export interface SeriesResponse {
  series: Series[]
  truncated: boolean
  /** Where the shared window ends when the point limit was reached. Every
   *  series is cut here, so the chart covers one period rather than each
   *  series covering its own. Null when nothing was truncated. */
  truncated_at: string | null
}

export interface ExportRequest {
  stations: string[]
  parameters: string[]
  from: string
  to: string
  format?: 'csv' | 'xlsx' | 'csv-excel'
  include_quality?: boolean
  include_missing?: boolean
  timezone?: string | null
  filename?: string | null
}

/** Every admin mutation returns this shape.
 *
 *  `warnings` is not decoration. The panel's edits reach backwards — changing
 *  a poll interval recomputes every past completeness percentage, remapping a
 *  channel leaves the measurements under the old parameter — and none of that
 *  is visible from the form being submitted. Surface them; never swallow them.
 */
export interface MutationResult<T> {
  warnings: string[]
  station?: T
  channel?: unknown
}

/** What /api/admin/stations returns — the `station_health` view. */
export type AdminStation = Station

/** The station row itself, from /api/admin/stations/{code}/settings.
 *
 *  A superset of the health view in the columns that matter for editing and a
 *  subset in the computed ones, so it is a separate type rather than an
 *  extension.
 */
export interface StationSettings {
  id: number
  code: string
  name: string
  external_id: string
  vendor: Vendor
  timezone: string
  poll_interval_s: number
  schedule_time: string
  catchup_days: number
  schedule_enabled: boolean
  retry_attempts: number
  retry_delay_min: number
  retention_start: string | null
  latitude: number | null
  longitude: number | null
  deactivated_at: string | null
  created_at: string
  updated_at: string
}

export interface StationWrite {
  code?: string
  name?: string
  external_id?: string
  timezone?: string
  poll_interval_s?: number
  schedule_time?: string
  catchup_days?: number
  schedule_enabled?: boolean
  retry_attempts?: number
  retry_delay_min?: number
  retention_start?: string | null
  latitude?: number | null
  longitude?: number | null
}

/** What a probe found, and what it proposes. Nothing is written yet. */
export interface StationPlan {
  name: string
  external_id: string
  vendor: Vendor
  code: string
  /** Null when the window was too sparse to measure a cadence — the operator
   *  has to supply it, since completeness for the whole archive is judged
   *  against it. */
  poll_interval_s: number | null
  schedule_time: string
  timezone: string
  rows_seen: number
  channels: PlannedChannel[]
  mapped: number
  unmapped: number
  warnings: string[]
}

export interface PlannedChannel {
  channel_id: number
  source_name: string
  source_unit: string
  /** Null means "left for the unmapped queue" — the safe default. */
  parameter: string | null
  event_driven: boolean
  reason: string
  stations_agreeing: number
}

/** A parameter with how heavily it is used. The counts are what make it safe
 *  to edit: one mapped nowhere and holding nothing can be changed freely. */
export interface AdminParameter extends Parameter {
  id: number
  max_rate: number | null
  mapped_channels: number
  enabled_channels: number
}

export interface ParameterWrite {
  code?: string
  display_name?: string
  unit?: string
  axis_group?: string
  kind?: ParameterKind
  min_valid?: number | null
  max_valid?: number | null
  max_rate?: number | null
  description?: string | null
}

export interface AdminChannel {
  channel_id: number
  parameter: string
  display_name: string
  unit: string
  kind: ParameterKind
  event_driven: boolean
  min_valid: number | null
  max_valid: number | null
  enabled: boolean
  source_name: string | null
  source_unit: string | null
  first_seen: string | null
  last_seen: string | null
}

export interface UnmappedChannel {
  id: number
  station: string
  channel_id: number
  source_name: string | null
  source_unit: string | null
  first_seen: string
  last_seen: string
  window_from: string | null
  window_to: string | null
  resolved_at: string | null
}

export interface AuditEntry {
  id: number
  at: string
  actor: string
  action: string
  entity: string | null
  entity_id: string | null
  detail: Record<string, unknown> | null
}

export interface CompletenessDay {
  station_code: string
  local_day: string
  distinct_timestamps: number
  expected_timestamps: number
  /** A Postgres `numeric`, which serialises as a string — parse it before
   *  doing arithmetic or a chart silently plots NaN. */
  pct_complete: string
}

/** [exceedance percent, value] — the value equalled or exceeded that
 *  percentage of the time. */
export type DurationPoint = [number, number]

export interface DurationCurve {
  station: string
  parameter: string
  points: DurationPoint[]
}

/** [stage, discharge, iso timestamp]. The timestamp is carried per point so
 *  the curve can be coloured by date, which is how rating drift shows up. */
export type RatingPoint = [number, number, string]

export interface RatingCurve {
  station: string
  points: RatingPoint[]
  /** True when the window held more pairs than the server returns: the
   *  earliest are here, the rest are not. The chart says so. */
  truncated: boolean
}

export interface WindRoseBin {
  /** 1-16 from `width_bucket(direction, 0, 360, 16)`. Sector n spans
   *  [(n-1) x 22.5, n x 22.5) degrees — it starts at the compass point, it is
   *  not centred on it. */
  direction_bin: number
  speed_bin: string
  count: number
}

export interface WindRose {
  station: string
  direction_bins: number
  bins: WindRoseBin[]
}

/** app_user.role. Held in the app database, not in Entra. */
export type Role = 'pending' | 'viewer' | 'admin' | 'disabled'

/** /api/auth/me — who this browser's session belongs to. */
export interface AuthUser {
  id: number
  email: string
  display_name: string | null
  role: Role
}

/** A row of /api/admin/users. */
export interface AppUser {
  id: number
  email: string
  display_name: string | null
  role: Role
  created_at: string
  last_seen_at: string | null
  approved_at: string | null
}

/** A row of /api/admin/runs — one collection, sweep, audit or backfill.
 *  For an audit, `rows_fetched` is readings compared and `error` carries its
 *  summary even when nothing went wrong. */
/** A vendor reading that disagrees with what `measurement` holds (db/016). */
export interface Revision {
  id: number
  station: string
  parameter: string
  display_name: string
  unit: string
  ts: string
  /** What `measurement` held when the vendor's reading arrived; for a
   *  duplicate, the reading of the two that was written. */
  stored_value: number | null
  stored_quality: number | null
  /** What the vendor sent and is not (or, for `replaced`, is now) stored. */
  vendor_value: number | null
  vendor_quality: number | null
  outcome: 'replaced' | 'kept' | 'duplicate'
  run_id: number | null
  seen_at: string
}

export interface IngestRun {
  id: number
  station: string | null
  started_at: string
  finished_at: string | null
  trigger: string
  from_ts: string | null
  to_ts: string | null
  status: 'pending' | 'ok' | 'empty' | 'partial' | 'failed'
  mds_error: string | null
  rows_fetched: number
  rows_written: number
  splits: number
  error: string | null
}

/** A logger's push login (GET /api/admin/stations/{code}/push-credentials).
 *  Never carries its secret. */
export interface PushLogin {
  id: number
  logger_id: string
  kind: 'basic' | 'path'
  created_at: string | null
  revoked_at: string | null
}

/** The one response that carries a secret: issue a login, show it, forget it. */
export interface IssuedPushLogin {
  id: number
  logger_id: string
  kind: 'basic' | 'path'
  secret: string
}

// ------------------------------------------------- alerts (redesign phase 4)

export type AlertScope = 'reading' | 'station'
export type AlertCondition = 'above' | 'below' | 'rate' | 'stale' | 'nodata' | 'gateway' | 'anyfast'
export type AlertNotify = 'warning' | 'alarm' | 'cleared'
export type AlarmLevel = 'warning' | 'alarm'

/** A rule as /api/alert-rules lists it: only the rules the reader manages. */
export interface AlertRule {
  id: number
  name: string
  scope: AlertScope
  condition: AlertCondition
  axis_group: string | null
  warn_level: number | null
  alarm_level: number
  hold_s: number
  message: string
  notify_on: AlertNotify[]
  enabled: boolean
  station: { code: string; name: string; vendor: Vendor }
  parameter: { code: string; name: string; unit: string } | null
  open: { level: AlarmLevel; since: string } | null
  targets: { users: { id: number; name: string }[]; projects: { id: number; name: string }[] }
  created_at: string
  updated_at: string
}

/** What POST /api/alert-rules takes; PATCH takes any part of it. */
export interface AlertRuleBody {
  name: string
  scope: AlertScope
  station: string
  parameter: string | null
  axis_group: string | null
  condition: AlertCondition
  warn_level: number | null
  alarm_level: number
  hold_s: number
  message: string
  notify_on: AlertNotify[]
  enabled: boolean
  targets: { users: number[]; projects: number[] }
}

/** What a rule on one station may name, for the reader. */
export interface AlertRuleOptions {
  station: { id: number; code: string; name: string; vendor: Vendor }
  parameters: { code: string; name: string; unit: string; axis_group: string }[]
  groups: string[]
  people: { id: number; name: string; email: string }[]
  projects: { id: number; name: string }[]
}

/** An open alarm or warning (/api/alarms). */
export interface Alarm {
  id: number
  level: AlarmLevel
  since: string
  escalated_at: string | null
  detail: Record<string, unknown>
  rule: { id: number; name: string; scope: AlertScope | 'strategy'; condition: AlertCondition | 'strategy'; message: string }
  /** Null for a strategy's alarm (db/036): it is its project's, and may read
   *  several stations, named in `stations`. */
  station: { code: string; name: string } | null
  parameter: { code: string; name: string; unit: string } | null
  project?: { id: number; name: string }
  stations?: { code: string; name: string }[]
  /** The newest note anyone left on it, "What is being done?"; null without
   *  one, or when it was written before the alarm rose to Alarm. */
  acknowledged?: AlarmAcknowledgement | null
}

export interface AlarmAcknowledgement { by: string; at: string; note: string }

/** Why an alarm's event ended: it cleared, its rule was switched off, or its
 *  rule was changed. Null for an event that has not ended. */
export type AlarmClosedReason = 'clear' | 'rule off' | 'rule changed'

/** One event in the signed-in person's alarm inbox (/api/me/alarm-inbox). */
export interface AlarmInboxEntry {
  id: number
  name: string
  level: 'warning' | 'alarm'
  opened_at: string
  closed_at: string | null
  escalated_at: string | null
  closed_reason: AlarmClosedReason | null
  /** When this person last checked it, and the note they left with it. */
  checked_at: string | null
  note: string | null
  station: string | null
  project: string | null
}

/** `active` is every open event in the person's scope; `closed` pages 100 at
 *  a time (`next_before` is the cursor for the next page). */
export interface AlarmInbox {
  active: AlarmInboxEntry[]
  closed: AlarmInboxEntry[]
  total: number
  unchecked: number
  next_before: number | null
}

/** An Alarm Lab strategy kept as a live rule (db/036, /api/strategies). */
export interface StrategyRecord {
  id: number
  name: string
  enabled: boolean
  notify_on: ('warning' | 'alarm' | 'cleared')[]
  /** The Lab's definition (lib/alarmLab.ts LabStrategy); its `id` is `rule-<id>`. */
  strategy: import('../lib/alarmLab').LabStrategy
  project: { id: number; name: string }
  open: { level: AlarmLevel; since: string } | null
  /** What the rule monitor made of it at its last round, while live. */
  status: { checked_at: string; state: 'clear' | 'pending' | 'active' | 'unknown'; level: AlarmLevel | null;
    reason: string } | null
  can_edit: boolean
  created_at: string
  updated_at: string
}

/** A strategy judged now (/api/strategies/{id}/verdict): the Lab's last frame. */
export interface StrategyVerdict {
  state: 'clear' | 'pending' | 'active' | 'unknown'
  level: AlarmLevel | null
  reason: string
  time?: number
}
/** One row of a rule's or strategy's history (GET .../history): the audit
 *  row the server wrote with the change, newest first. An update's `detail`
 *  holds `before` and `after` for the fields that changed. */
export interface RuleHistoryEntry {
  at: string
  actor: string
  action: string
  detail: Record<string, unknown>
}

export interface StrategyEvent {
  id: number
  level: AlarmLevel
  opened_at: string
  escalated_at: string | null
  closed_at: string | null
  closed_reason: string | null
}

export interface NotificationPref {
  email: boolean
  receives: 'both' | 'alarm'
  configured?: boolean
}

export interface NotificationSettings {
  remind_every_h: 0 | 1 | 4 | 12 | 24
  digest: boolean
  digest_at?: string
}

/** Escalation of unacknowledged alarms (db/048): who is sent a second and a
 *  third notice, and after how many hours. `people` are user ids. */
export interface EscalationStep {
  after_h: number
  people: number[]
}

export interface NotificationEscalation {
  enabled: boolean
  second: EscalationStep
  third: EscalationStep
}

export interface NotificationRecipients {
  people: { id: number; name: string; email: string; role: Role; email_on: boolean;
            receives: 'both' | 'alarm' }[]
  projects: { id: number; name: string; members: number }[]
}

export interface OutboxEmail {
  id: number
  kind: string
  to_email: string
  subject: string
  status: 'pending' | 'sent' | 'failed' | 'skipped'
  attempts: number
  created_at: string
  sent_at: string | null
  last_error: string | null
}

export interface MailStatus {
  configured: boolean
  sender: string | null
  last_sent_at: string | null
  failed_24h: number
}

// ------------------------------------------ project chat and images (phase 5)

export interface ProjectImageUrls { thumb: string; preview: string; original: string }

export interface ProjectMessage {
  id: number
  kind: 'text' | 'event'
  body: string
  level: 'warning' | 'alarm' | 'cleared' | null
  created_at: string
  author: string | null
  station: { code: string; name: string } | null
  rule: string | null
  image: { id: number; caption: string; urls: ProjectImageUrls } | null
  can_remove: boolean
}

export interface ProjectImage {
  /** "i{id}" for an uploaded image, "c{name}" for a camera photo. */
  id: string
  via: 'upload' | 'chat' | 'camera'
  caption: string
  original_name: string | null
  media_type: string
  bytes: number | null
  width: number | null
  height: number | null
  created_at: string
  by: string | null
  station: { code: string; name: string } | null
  urls: ProjectImageUrls
  can_remove: boolean
}

// ------------------------------------------------------- sensors (phase 6)

/** What a person can say a sensor is (db/032). */
export const SENSOR_TYPES = [
  'Piezometer', 'Inclinometer', 'Extensometer', 'Tiltmeter', 'Crackmeter', 'GNSS', 'Prism',
  'Rain gauge', 'Temperature', 'Settlement gauge', 'Water level', 'Weather station', 'Other',
] as const
export type SensorType = typeof SENSOR_TYPES[number]

// ------------------------------------------------ manual sensors (db/034)

/** A gauge read by hand, as GET /api/manual-sensors describes it. It
 *  belongs to a project; `can_edit` (its Editors and Admins) changes the
 *  sensor, `can_enter` (any member) enters readings. `id` is its key, the
 *  `manual|<id>` of formulas and analyses. */
export interface ManualSensorRecord {
  id: string
  name: string
  sensor_type: SensorType
  /** What is read, in the reader's words: "Water level", "Crack width". */
  quantity: string
  unit: string
  precision: number
  axis_group: string
  notes: string
  project_id: number
  project: string
  station: string | null
  station_name: string | null
  created_at: string
  updated_at: string
  reading_count: number
  latest: { ts: string; value: number } | null
  can_edit: boolean
  can_enter: boolean
}

/** What the create and edit forms send. */
export interface ManualSensorWrite {
  project_id: number
  name: string
  sensor_type: SensorType
  quantity: string
  unit: string
  precision: number
  station: string | null
  notes: string
}

/** One stored reading. `ts` is station wall clock with its offset, the one
 *  spelling of its instant (2026-10-22T14:30:00+04:00). */
export interface ManualReadingRecord {
  id: number
  ts: string
  value: number
  note: string
  origin: 'typed' | 'import'
  entered_at: string
  entered_by: string
}

export interface ManualReadingRevision {
  id: number
  reading_id: number
  action: 'replaced' | 'edited' | 'deleted'
  old_ts: string
  old_value: number
  old_note: string
  new_ts: string | null
  new_value: number | null
  new_note: string | null
  at: string
  by: string
}

// ------------------------------------- calculated sensors, flags (db/035)

/** What the calculated sensor forms send. */
export interface CalculatedSensorWrite {
  project_id: number
  name: string
  formula: string
  mode: 'expression' | 'program'
  unit: string
  axis_group: string
  precision: number
  inputs: { alias: 'A' | 'B' | 'C'; sourceId: string }[]
}

/** An Overview issue someone flagged as known, shared with everyone (db/035,
 *  db/043 to db/045). `occurrence` is the incident it was made for and
 *  `expires_at` the server's own end of it (a late reading only); `null` for
 *  either on a flag from before they existed. */
export interface ReviewFlag {
  issue_key: string; flagged_at: string; by: string
  note: string; occurrence: string | null; expires_at: string | null
  /** Someone went and looked: who, when, and what they found. */
  checked_at: string | null; checked_by: string | null; checked_note: string | null
}

export type SensorStatusReason = NoDataReason | 'sensor_silent' | 'sensor_never_read'

/** A gateway node, or an MDS or MRL station's logger (key "logger"), as
 *  GET /api/sensors lists it. Its status is the api's verdict. */
export interface Sensor {
  key: string
  instrument_id: number | null
  name: string
  model: string | null
  station: { code: string; name: string; vendor: Vendor; projects: string[] }
  sensor_type: SensorType | null
  /** Typed latitude and longitude, or a UTM survey converted; null when
   *  neither is complete. */
  position: { lat: number; lon: number; source: 'entered' | 'utm' } | null
  elevation_m: number | null
  active: boolean | null
  status: 'receiving' | 'no_data'
  status_reason: SensorStatusReason | null
  newest_reading_at: string | null
  files: { image: number; document: number }
}

export interface SensorProperties {
  sensor_type: SensorType | null
  latitude: number | null
  longitude: number | null
  height_m: number | null
  elevation_m: number | null
  northing_m: number | null
  easting_m: number | null
  crs_epsg: 32638 | 32637 | 4326 | null
  active: boolean | null
  reference_mode: 'oldest' | 'day' | null
  reference_day: string | null
  notes: string
}

/** What PATCH takes: latitude and longitude as typed text. */
export type SensorPatch = Partial<Omit<SensorProperties, 'latitude' | 'longitude'> & {
  latitude: string | null
  longitude: string | null
}>

export interface SensorFile {
  id: number
  kind: 'image' | 'document'
  name: string
  media_type: string
  bytes: number
  width: number | null
  height: number | null
  /** What the file is, from its name: decides the badge and the filter. */
  family: FileFamily
  /** A real PDF or a plain image, which opens in the page; anything else
   *  downloads. */
  inline: boolean
  /** Other sensors holding the same stored file (Sensor Docs). */
  shared_with: number
  uploaded_by: string | null
  created_at: string
  urls: { thumb?: string; preview?: string; original: string }
}

export type FileFamily = 'pdf' | 'word' | 'excel' | 'drawing' | 'image' | 'archive' | 'other'

/** A project's own file (GET /api/projects/{id}/documents). */
export interface ProjectDocument {
  id: number
  name: string
  media_type: string
  family: FileFamily
  bytes: number
  created_at: string
  by: string
  inline: boolean
  can_remove: boolean
  urls: { original: string }
}

/** One file on the project's sensors (Sensor Docs): a share sent to many
 *  ("s{id}"), or a file attached on one sensor's Sensor info ("f{id}"). */
export interface SensorDoc {
  key: string
  name: string
  kind: 'image' | 'document'
  family: FileFamily
  media_type: string
  bytes: number
  created_at: string
  by: string
  via: 'sensor-docs' | 'sensor-info'
  inline: boolean
  urls: { thumb?: string; preview?: string; original: string }
  /** Only the sensors of this project's stations; ref is "{station}/{key}". */
  sensors: { ref: string; name: string; station: { code: string; name: string }; file_id: number }[]
}

export interface SensorDocsList {
  docs: SensorDoc[]
  sensors: Sensor[]
  can_manage: boolean
}

export interface SensorDocResult {
  doc: SensorDoc | null
  attached: number
  already: number
  refused: { name: string; reason: string }[]
}

export interface SensorDetail {
  sensor: Sensor
  properties: SensorProperties
  updated_at: string | null
  updated_by: string | null
  files: SensorFile[]
  can_edit: boolean
  health_checked_at: string | null
}

/** One channel Explore sensors can chart (GET /api/compare/channels). */
export interface CompareChannel {
  station: string
  station_name: string
  vendor: Vendor
  instrument_id: number | null
  sensor: string | null
  parameter: string
  display_name: string
  unit: string
  axis_group: string
  kind: ParameterKind
  precision: number | null
  /** Silent between events by design, so silence earns it no freshness verdict. */
  event_driven?: boolean
}

/** Collect now (db/037): the station's latest request and whether another
 *  may be made. The cooldown is the server's, whoever asks. */
export interface CollectRequest {
  id: number
  requested_by: string
  requested_at: string
  started_at: string | null
  finished_at: string | null
  status: 'pending' | 'running' | 'ok' | 'partial' | 'empty' | 'failed'
  window_from: string | null
  window_to: string | null
  rows_written: number | null
  error: string | null
}

export interface CollectStatus {
  station: string
  available: boolean
  /** Why this station cannot be asked: a push station, collection off. */
  refusal: string | null
  cooldown_s: number
  can_request: boolean
  again_at: string | null
  /** "MDS" or "the gateway": what a request asks */
  source: string
  /** Since when the health checks have had that source failing, if they do */
  source_down_since: string | null
  latest: CollectRequest | null
}

/** One gateway's rounds (db/037), for the admin panel. */
export interface GatewayTiming {
  round_minutes: number[] | null
  retry_gap_s: number | null
  retries: number | null
}

export interface GatewaySchedule {
  code: string
  name: string
  collecting: boolean
  override: GatewayTiming
  learned: {
    round_minutes: number[] | null
    streams: number | null
    learned_at: string | null
    /** round minute -> how many reading streams it waits for */
    expected: Record<string, number>
  }
  effective: { round_minutes: number[]; retry_gap_s: number; retries: number }
  updated_by: string | null
  updated_at: string | null
  last_day: { rounds: number; complete: number; gave_up: number;
              reads_avg: number | null; reads: number } | null
  latest_round: { start: string; reads: number; expected: number; reported: number;
                  /** "node/message type", e.g. "202631/gnssData" */
                  missing: string[]; outcome: string | null; ended_at: string | null } | null
}

export interface GatewaySchedules {
  defaults: { retry_gap_s: number; retries: number }
  gateways: GatewaySchedule[]
}

/** The AI access page (backend/app/mcp/routes.py). */
export interface AiAccessOverview {
  url: string
  everyone: boolean
  grants: { id: number; client: string; email: string; created_at: string; expires_at: string; last_used_at: string | null }[]
  calls: { id: number; at: string; email: string; client: string | null; tool: string; outcome: 'ok' | 'refused' | 'error'; error: string | null; duration_ms: number }[]
}
